import React, { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, ImagePlus } from "lucide-react";
import { api } from "@/lib/api";

const rotations = ["-rotate-2", "rotate-1", "rotate-2", "-rotate-1", "rotate-0"];

export default function Gallery({ photos, setPhotos }) {
  const [open, setOpen] = useState(false);
  const [caption, setCaption] = useState("");
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);

  const onFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!preview) return;
    setSaving(true);
    const { data } = await api.post("/photos", { image: preview, caption });
    setPhotos((p) => [data, ...p]);
    setSaving(false);
    setOpen(false);
    setPreview("");
    setCaption("");
  };

  const remove = async (id) => {
    await api.delete(`/photos/${id}`);
    setPhotos((p) => p.filter((x) => x.id !== id));
  };

  return (
    <section id="gallery" className="max-w-6xl mx-auto px-6 py-16">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="font-hand text-2xl text-[#D95F59]">moments we keep</p>
          <h2 className="font-display text-4xl tracking-tight">Our Photo Wall</h2>
        </div>
        <button
          data-testid="add-photo-btn"
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 rounded-full bg-[#D95F59] text-white px-5 py-2.5 text-sm hover:bg-[#C75D53] hover:scale-105 transition-all"
        >
          <Plus className="w-4 h-4" /> Add a memory
        </button>
      </div>

      {photos.length === 0 ? (
        <div className="card-paper rounded-3xl p-14 text-center text-[#756D6A]">
          <ImagePlus className="w-10 h-10 mx-auto mb-3 text-[#D95F59]" />
          <p className="font-hand text-2xl">No photos yet — add your first memory together 💕</p>
        </div>
      ) : (
        <div className="columns-2 md:columns-3 gap-5 [&>*]:mb-5">
          {photos.map((ph, i) => (
            <motion.div
              key={ph.id}
              initial={{ opacity: 0, scale: 0.92 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              data-testid="photo-card"
              className={`break-inside-avoid card-paper rounded-xl p-3 ${rotations[i % rotations.length]} hover:rotate-0 hover:-translate-y-1 transition-all duration-400 group relative`}
            >
              <img src={ph.image} alt={ph.caption} className="w-full rounded-lg object-cover" />
              {ph.caption && (
                <p className="font-hand text-xl text-center mt-2 px-1">{ph.caption}</p>
              )}
              <button
                data-testid="delete-photo-btn"
                onClick={() => remove(ph.id)}
                className="absolute top-5 right-5 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 className="w-4 h-4 text-[#D95F59]" />
              </button>
            </motion.div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div
            className="card-paper rounded-3xl p-7 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
            data-testid="add-photo-modal"
          >
            <h3 className="font-display text-2xl mb-4">Add a memory</h3>
            <label className="block border-2 border-dashed border-[#EADEDA] rounded-2xl p-6 text-center cursor-pointer hover:bg-[#FFF5F5] transition-colors">
              {preview ? (
                <img src={preview} alt="preview" className="w-full h-44 object-cover rounded-xl" />
              ) : (
                <span className="text-[#756D6A] flex flex-col items-center gap-2">
                  <ImagePlus className="w-8 h-8 text-[#D95F59]" /> Click to choose a photo
                </span>
              )}
              <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="photo-file-input" />
            </label>
            <input
              data-testid="photo-caption-input"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Write a sweet caption…"
              className="w-full mt-4 rounded-full border border-[#EADEDA] px-4 py-2.5 bg-[#FFF5F5] outline-none focus:border-[#D95F59]"
            />
            <div className="flex gap-3 mt-5">
              <button onClick={() => setOpen(false)} className="flex-1 rounded-full border border-[#EADEDA] py-2.5 text-[#756D6A] hover:bg-[#F4F1EA]">
                Cancel
              </button>
              <button
                data-testid="save-photo-btn"
                onClick={save}
                disabled={!preview || saving}
                className="flex-1 rounded-full bg-[#D95F59] text-white py-2.5 hover:bg-[#C75D53] disabled:opacity-50"
              >
                {saving ? "Saving…" : "Keep this 💕"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
